'use client';

import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme, alpha } from '@mui/material/styles';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import { BOT_IDEAS, shuffleBotIdeas } from '../content/botIdeas';
import { trackInspirationFeedViewed, trackInspirationIdeaClicked } from '../../../lib/analytics';
import type { BotIdea } from '../content/botIdeas';

/**
 * Bot Inspiration Feed
 *
 * A ticker-style feed of bot idea chips that scrolls continuously.
 * Supports vertical (for VariantC) and horizontal (for VariantA/B) layouts.
 *
 * Features:
 * - CSS-only infinite scroll animation (GPU-composited, 60fps)
 * - Hover/touch pause
 * - Reduced motion: static grid with shuffle button
 * - RTL support with reversed animation direction
 * - Click-to-suggest interaction
 *
 * Story 6.13: Bot Inspiration Feed on Landing Page
 */

interface BotInspirationFeedProps {
  variant: 'vertical' | 'horizontal';
  onIdeaClick?: ((idea: BotIdea) => void) | undefined;
  pageVariant?: string | undefined;
  feedPosition?: string | undefined;
}

export function BotInspirationFeed({ variant, onIdeaClick, pageVariant, feedPosition }: BotInspirationFeedProps) {
  const t = useTranslations('creator.landing');
  const theme = useTheme();
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  const [ideas, setIdeas] = useState<BotIdea[]>(() => shuffleBotIdeas(BOT_IDEAS));

  // Impression tracking — fire once per session (AC8)
  const hasTrackedImpression = useRef(false);
  useEffect(() => {
    if (!hasTrackedImpression.current) {
      hasTrackedImpression.current = true;
      trackInspirationFeedViewed(
        pageVariant ?? 'unknown',
        feedPosition ?? variant,
        BOT_IDEAS.length,
      );
    }
  }, [pageVariant, feedPosition, variant]);

  const handleShuffle = useCallback(() => {
    setIdeas(shuffleBotIdeas(BOT_IDEAS));
  }, []);

  const handleChipClick = useCallback(
    (idea: BotIdea) => {
      // Track click with correct feedPosition from this component's props (AC4/AC8)
      trackInspirationIdeaClicked(
        idea.nameKey,
        idea.category,
        pageVariant ?? 'unknown',
        feedPosition ?? variant,
      );
      onIdeaClick?.(idea);
    },
    [onIdeaClick, pageVariant, feedPosition, variant],
  );

  const chipSx = useMemo(
    () => ({
      backgroundColor: alpha(theme.palette.primary.main, 0.06),
      color: theme.palette.primary.dark,
      borderRadius: 3,
      height: '32px',
      cursor: 'pointer',
      transition: 'all 0.15s ease',
      '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.12),
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        transform: 'scale(1.02)',
      },
    }),
    [theme],
  );

  // Reduced motion: render static grid with shuffle button
  if (prefersReducedMotion) {
    return (
      <Box
        role="region"
        aria-label={t('inspirationFeed.title')}
        sx={{ py: 2 }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {t('inspirationFeed.title')}
          </Typography>
          <IconButton
            onClick={handleShuffle}
            size="small"
            aria-label={t('inspirationFeed.shuffleButton')}
          >
            <ShuffleIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          {ideas.slice(0, 12).map((idea) => (
            <Chip
              key={idea.id}
              label={`${idea.emoji} ${t(`inspirationFeed.ideas.${idea.nameKey.split('.').pop()}`)}`}
              size="small"
              onClick={() => handleChipClick(idea)}
              sx={chipSx}
            />
          ))}
        </Box>
      </Box>
    );
  }

  // Animated ticker
  const isVertical = variant === 'vertical';
  const visibleIdeas = ideas.slice(0, 30);

  // Horizontal: use position absolute so the wide track doesn't affect parent layout
  if (!isVertical) {
    return (
      <Box role="region" aria-label={t('inspirationFeed.title')} sx={{ width: '100%', minWidth: 0 }}>
        {/* Emphasis label so visitors know the chips are clickable starting ideas (Story 19.52) */}
        <Typography
          variant="caption"
          sx={{ display: 'block', mb: 0.5, fontWeight: 600, color: 'primary.main', textAlign: 'center' }}
        >
          {t('inspirationFeed.tapHint')}
        </Typography>
        <Box
          sx={{
            position: 'relative',
            overflow: 'hidden',
            height: 40,
            width: '100%',
            minWidth: 0,
          }}
        >
          <Box
            aria-live="off"
            className="ticker-track"
            sx={{
              // Force LTR layout so flex packing + the translate loop stay consistent
              // under RTL (otherwise the track packs off-screen and leaves an empty
              // strip). The chip labels still render their own bidi text correctly.
              direction: 'ltr',
              position: 'absolute',
              top: 0,
              left: 0,
              display: 'flex',
              flexDirection: 'row',
              gap: 1,
              alignItems: 'center',
              height: '100%',
              width: 'max-content',
              whiteSpace: 'nowrap',
              animation: 'tickerHorizontal 120s linear infinite',
              '&:hover': { animationPlayState: 'paused' },
              '@keyframes tickerHorizontal': {
                '0%': { transform: 'translateX(0%)' },
                '100%': { transform: 'translateX(-50%)' },
              },
            }}
          >
            {visibleIdeas.map((idea) => (
              <Chip
                key={idea.id}
                label={`${idea.emoji} ${t(`inspirationFeed.ideas.${idea.nameKey.split('.').pop()}`)}`}
                size="small"
                onClick={() => handleChipClick(idea)}
                sx={{ ...chipSx, flexShrink: 0 }}
              />
            ))}
            {visibleIdeas.map((idea) => (
              <Chip
                key={`dup-${idea.id}`}
                label={`${idea.emoji} ${t(`inspirationFeed.ideas.${idea.nameKey.split('.').pop()}`)}`}
                size="small"
                onClick={() => handleChipClick(idea)}
                sx={{ ...chipSx, flexShrink: 0 }}
              />
            ))}
          </Box>
        </Box>
      </Box>
    );
  }

  // Vertical ticker
  return (
    <Box role="region" aria-label={t('inspirationFeed.title')}>
      {/* Emphasis label so visitors know the chips are clickable starting ideas (Story 19.52) */}
      <Typography
        variant="caption"
        sx={{ display: 'block', mb: 0.5, fontWeight: 600, color: 'primary.main', textAlign: 'center' }}
      >
        {t('inspirationFeed.tapHint')}
      </Typography>
      <Box
        sx={{
          overflow: 'hidden',
          py: 1,
          maxHeight: 90,
        }}
      >
        <Box
          aria-live="off"
          className="ticker-track"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            alignItems: 'center',
            animation: 'tickerVertical 80s linear infinite',
            '&:hover': { animationPlayState: 'paused' },
            '@keyframes tickerVertical': {
              '0%': { transform: 'translateY(0%)' },
              '100%': { transform: 'translateY(-50%)' },
            },
          }}
        >
          {visibleIdeas.map((idea) => (
            <Chip
              key={idea.id}
              label={`${idea.emoji} ${t(`inspirationFeed.ideas.${idea.nameKey.split('.').pop()}`)}`}
              size="small"
              onClick={() => handleChipClick(idea)}
              sx={{ ...chipSx, flexShrink: 0 }}
            />
          ))}
          {visibleIdeas.map((idea) => (
            <Chip
              key={`dup-${idea.id}`}
              label={`${idea.emoji} ${t(`inspirationFeed.ideas.${idea.nameKey.split('.').pop()}`)}`}
              size="small"
              onClick={() => handleChipClick(idea)}
              sx={{ ...chipSx, flexShrink: 0 }}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
}
